'use client';

// imports
;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:dotorg-next":true});}catch(e){}}();import Link from "next/link";
import { useState, useRef, useEffect } from "react";
import { useAtom } from "jotai";
import { useRouter } from 'next/navigation';

// lib files
import { isAlphaOnly } from "@/lib/helpers/is-alpha-only";
import { isValidMembershipTokenFormat } from "@/lib/helpers/is-valid-membership-token-format";
import { PASSPORT_LOOKUP_URL } from '@/lib/constants';
import { passportActivationAtom, defaultPassportActivateState } from "@/lib/atoms/passport-activation";

// svgs
import CloseIcon from '@/public/svg/close.svg';

// styles
import styles from './MembershipTokenInput.module.scss';

const MEMBERSHIP_TOKEN_ERROR_MESSAGE = {
  INVALID_FORMAT: "Codes are composed of four words separated by hyphens (example: when-course-human-events).",
  ALPHA_ONLY: "Codes can only contain letters and hyphens.",
}

interface MembershipTokenInputProps {
  startingErrorMessage?: string | null;
}

const MembershipTokenInput = (props: MembershipTokenInputProps) => {
  const [ membershipTokenIsValid, setMembershipTokenIsValid ] = useState(false);
  const [ _, setPassportActivateState ] = useAtom(passportActivationAtom);
  const [ errorMessage, setErrorMessage ] = useState<string | null>(props.startingErrorMessage || null);

  const inputRef = useRef<HTMLInputElement>(null);
  const router = useRouter();

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const inputValue = inputRef.current?.value;

    if (membershipTokenIsValid && inputValue) {
      setPassportActivateState(prevState => ({
        ...prevState,
        membershipToken: inputValue,
      }))
      // Send the user to the next step in the passport activation flow
      // to the endpoint /passport/activate/[membershipToken]
      router.push(`/passport/activate/${inputValue}/`);
    }
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    // Client side validation
    switch(true) {
      case inputValue.length > 0 && !isAlphaOnly(inputValue):
        setMembershipTokenIsValid(false);
        setErrorMessage(MEMBERSHIP_TOKEN_ERROR_MESSAGE.ALPHA_ONLY);
        break;
      default:
        setMembershipTokenIsValid(isValidMembershipTokenFormat(inputValue));
        setErrorMessage(null);
    }
  };

  // Clear the passport activation state from session storage whenever
  // the user comes to the /passport page.
  useEffect(() => {
    setPassportActivateState(defaultPassportActivateState);
  }, [setPassportActivateState]);

  return (
    <div className={styles.membership_token_input} data-sentry-component="MembershipTokenInput" data-sentry-source-file="MembershipTokenInput.tsx">
      <h1 className={styles.membership_token_input__headline}>Finish Setting Up Passport</h1>
      <p className={styles.membership_token_input__text}>
        Please enter the code provided by your local station to stream even more of your favorite PBS shows.
      </p>
      <div className={styles.membership_token__form_wrapper}>
        <form action="" onSubmit={handleSubmit} className={styles.membership_token_input__form}>
          <label htmlFor="membershipToken" className="visuallyhidden">Sign In Code</label>
          <input
            type="text"
            name="membershipToken"
            id="membershipToken"
            required
            placeholder="ex: quick-brown-fox-jump"
            onChange={handleChange}
            ref={inputRef}
            className={styles.membership_token_input__input}
            autoComplete="off"
          />
          <button
            type="submit"
            disabled={!membershipTokenIsValid}
            className={styles.membership_token_input__button}
          >
            Continue
          </button>
        </form>
        {errorMessage && (
          <p
            className={styles.membership_token_error}
          >
          <CloseIcon />
          <span dangerouslySetInnerHTML={{ __html: errorMessage }}></span>
          </p>
        )}
      </div>

      <Link
        href={PASSPORT_LOOKUP_URL}
        className={styles.membership_token_dont_know_your_code_link}
        prefetch={false}
        data-sentry-element="Link" data-sentry-source-file="MembershipTokenInput.tsx">
        Don&rsquo;t know your code?
      </Link>
    </div>
  );
};

export default MembershipTokenInput;
